import React, { useState } from "react";
import styled from "styled-components";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";


const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.0rem;
  padding: 2rem;
  border-radius: 8px;
  background: linear-gradient(135deg, #ffffff, #f8f9fa);
  box-shadow: 0px 8px 30px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 400px;
  margin: 2rem auto;
  overflow-y: auto;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE 10+ */

  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }

  h2 {
    margin-bottom: 0.5rem;
    color: #2a4365;
    font-size: 1.8rem;
    font-weight: 700;
    text-align: center;
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    input,
    select,
    textarea {
      padding: 0.8rem;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      font-size: 1rem;
      margin-bottom: 1rem;
      outline: none;
      background-color: #f7fafc;
      transition: all 0.3s ease;

      &::placeholder {
        color: #a0aec0;
      }

      &:focus {
        border-color: #ff5757;
        background-color: #ffffff;
        box-shadow: 0px 4px 10px rgba(255, 87, 87, 0.2);
      }
    }

    textarea {
      resize: none;
    }
  }

  .buttons {
    display: flex;
    justify-content: space-between;

    button {
      padding: 0.8rem 1.8rem;
      border: none;
      border-radius: 12px;
      font-size: 1rem;
      font-weight: 700;
      cursor: pointer;
      transition: all 0.3s ease;
      text-align: center;

      &:first-child {
        background: linear-gradient(135deg, #ff6b6b, #ff4040);
        color: #ffffff;
        box-shadow: 0px 6px 15px rgba(255, 87, 87, 0.3);

        &:hover {
          background: linear-gradient(135deg, #ff4040, #e04040);
          box-shadow: 0px 8px 20px rgba(255, 87, 87, 0.4);
        }
      }

      &:last-child {
        background: linear-gradient(135deg, #e2e8f0, #cbd5e0);
        color: #2d3748;

        &:hover {
          background: linear-gradient(135deg, #cbd5e0, #a0aec0);
          box-shadow: 0px 6px 15px rgba(160, 174, 192, 0.3);
        }
      }
    }
  }

  .error-message {
    color: #e53e3e;
    font-size: 0.9rem;
    text-align: center;
    margin-top: -0.5rem;
  }

  .success-message {
    color: #38a169;
    font-size: 1rem;
    text-align: center;
    margin-bottom: 1rem;
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
    max-width: 90%;
  }

  @media (max-width: 480px) {
    padding: 1rem;
    max-width: 100%;
    margin: 1rem;
  }
`;

const MySwal = withReactContent(Swal);
const RegistrationForm = ({ eventId, title, onClose }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    contact: "",
    education_level: "",
    message: "",
    event_id: eventId,
  });

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
  
    if (!formData.name || !formData.email || !formData.contact || !formData.education_level) {
      MySwal.fire({
        icon: "error",
        title: "Oops...",
        text: "All fields are required!",
        customClass: {
          popup: "swal-custom-popup",
          title: "swal-title",
          confirmButton: "swal-confirm-button",
        },
      });
      return;
    }
  
    fetch("https://pristineacademicconsultancy.com/backend/send_registration_email.php", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(formData),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          MySwal.fire({
            icon: "success",
            title: "Registration Successful!",
            text: "You have been registered successfully.",
            customClass: {
              popup: "swal-custom-popup",
              title: "swal-title",
              confirmButton: "swal-confirm-button",
            },
          });
  
          setFormData({
            name: "",
            email: "",
            contact: "",
            education_level: "",
            message: "",
            event_id: eventId,
          });
        } else {
          MySwal.fire({
            icon: "error",
            title: "Error!",
            text: data.message || "An error occurred. Please try again later.",
            customClass: {
              popup: "swal-custom-popup",
              title: "swal-title",
              confirmButton: "swal-confirm-button",
            },
          });
        }
      })
      .catch(() => {
        MySwal.fire({
          icon: "error",
          title: "Error!",
          text: "An error occurred. Please try again later.",
          customClass: {
            popup: "swal-custom-popup",
            title: "swal-title",
            confirmButton: "swal-confirm-button",
          },
        });
      });
  };
  return (
    <FormContainer>
      <h2>{title}</h2>
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Your Name"
          />
        </div>
        <div className="form-group">
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Your Email"
          />
        </div>
        <div className="form-group">
          <input
            type="text"
            name="contact"
            value={formData.contact}
            onChange={handleChange}
            placeholder="Contact Number"
          />
        </div>
        <div className="form-group">
          <select
            name="education_level"
            value={formData.education_level}
            onChange={handleChange}
          >
            <option value="" disabled>
              Education Level
            </option>
            <option value="High School">High School</option>
            <option value="Undergraduate">Undergraduate</option>
            <option value="Postgraduate">Postgraduate</option>
            <option value="Doctorate">Doctorate</option>
          </select>
        </div>
        <div className="form-group">
          <textarea
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder="Your Message (Optional)"
            rows="3"
          ></textarea>
        </div>
        <div className="buttons">
          <button type="submit">Submit Registration</button>
         
        </div>
      </form>
    </FormContainer>
  );
};

export default RegistrationForm;
